import React, { useMemo } from "react";
import type { MenuProps } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import IconDropdownButton from "./IconDropdownButton";
import { divider, group, item } from "./menu";

// use your existing IconFont (legacy)
import { IconFont } from "../../../component/icon-font";

type Device = { label: string; deviceId: string };

type Props = {
  disabled?: boolean;
  isStartedAudio: boolean;
  isMuted: boolean;
  audioType?: string; // optional, but keep if you want (e.g. "computer")
  micList: Device[];
  speakerList: Device[];
  activeMicId?: string;
  activeSpeakerId?: string;

  onToggle: () => void;
  onSelect: (key: string) => void; // expects "microphone|id" or "speaker|id"
};

export default function MicControl({
  disabled,
  isStartedAudio,
  isMuted,
  micList,
  speakerList,
  activeMicId,
  activeSpeakerId,
  onToggle,
  onSelect,
}: Props) {
  const tooltip = isStartedAudio ? (isMuted ? "Unmute" : "Mute") : "Start audio";

  const icon = useMemo(() => {
    if (!isStartedAudio) return <IconFont type="icon-participant-audio-muted" />;
    if (isMuted) return <IconFont type="icon-audio-muted" />;
    return <IconFont type="icon-audio-unmuted" />;
  }, [isStartedAudio, isMuted]);

  const menuItems: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [];

    if (micList?.length) {
      items.push(
        group(
          "Select a microphone",
          micList.map((d) => item(d.label || d.deviceId, `microphone|${d.deviceId}`, d.deviceId === activeMicId ? <CheckOutlined /> : undefined))
        )
      );
      items.push(divider);
    }

    if (speakerList?.length) {
      items.push(
        group(
          "Select a speaker",
          speakerList.map((d) => item(d.label || d.deviceId, `speaker|${d.deviceId}`, d.deviceId === activeSpeakerId ? <CheckOutlined /> : undefined))
        )
      );
    }

    return items.length ? items : undefined;
  }, [micList, speakerList, activeMicId, activeSpeakerId]);

  return (
    <IconDropdownButton
      tooltip={tooltip}
      icon={icon}
      onClick={onToggle}
      menuItems={menuItems}
      onMenuClick={({ key }) => onSelect(String(key))}
      disabled={disabled}
      className="microphone-footer"
    />
  );
}
